import { Accordion, AccordionDetails, AccordionSummary, ButtonBase, Collapse } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import classNames from 'classnames'
import { useEffect, useState } from 'react'
import { FileImportDialog } from '../components/dialogs/FileImportDialog'
import { useTranslation } from 'react-i18next'
import { useTraderDiaryApi } from '../hooks/useTraderDiaryApi'
import { useDispatch } from 'react-redux'
import { ExpandMoreRounded as ExpandMore, AddRounded, WarningAmberRounded } from '@mui/icons-material'
import DownloadIcon from '@mui/icons-material/DownloadRounded'
import useMobileWidth from '../hooks/useMobileWidth'
import { newThemeObject } from '../newTheme'
import { NewLayout } from '../components/newLayout/NewLayout'
import { AlertMessageConfirmation } from '../components/dialogs/AlertMessageConfirmation'
import { TraderDiaryTiles } from '../components/common/TraderDiaryTiles'
import { TraderDiaryTableTopBar } from '../components/TraderDiaryList/TraderDiaryTableTopBar'
import { TraderDiaryDisplay } from '../components/TraderDiaryList/TraderDiaryDisplay'
import { useTraderDiaryFilters } from '../hooks/useTraderDiaryFilters'
import { exportTraderDiariesAction } from '../redux/actions/traderDiaryActions'
import { TradeDiaryInputDialog } from '../components/dialogs/TraderDiaryInputDialog'
import { tokens } from '../styles/tokens'
import { dayStart, dayEnd } from '../utils/dateRange'
import { useSelector } from '../redux/hooks/useSelector'
import { openDialog } from '../redux/actions/dialogActions'
import { TRADER_DIARY_INPUT_DIALOG_ID } from '../constants/dialogConstants'
import { ColoredIconButton } from '../components/common/ColoredIconButton'

const useStyles = makeStyles(theme => ({
  orderDisplayTable: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: theme.spacing(2),
    flexGrow: 1,
  },
  clientEssentials: {
    width: '25%',
    display: 'flex',
    flexDirection: 'row',
    flexGrow: 1,
  },
  manIcon: {
    marginTop: theme.spacing(1),
  },
  clientPersonalInfoColumn: {
    display: 'flex',
    flexDirection: 'column',
    margin: theme.spacing(1),
  },
  test: {
    background: 'red',
    width: '200px',
    position: 'absolute',
    top: '20px',
    left: '-50px',
    opacity: '0.95',
    textAlign: 'center',
    lineHeight: '25px',
    fontSize: '16px',
    letterSpacing: '1px',
    color: '#f0f0f0',
    transform: 'rotate(-45deg)',
    paddingRight: '18px',
  },

  searchBar: {
    background: tokens.color.white,
    border: `1px solid ${tokens.color.neutral[200]}`,
    boxShadow: 'none',
    padding: 0,
    borderRadius: tokens.radius.lg,
    margin: '16px 0',
    borderTop: 0,
    '&.Mui-expanded': {
      margin: '16px 0',
    },
    '&::before': {
      display: 'none',
    },
    // Na desktopu je akordeon jen neviditelny obal - viditelna karta je vnitrni
    // toolbar (TraderDiaryTableTopBar), stejny vzor jako Klienti/Instrumenty.
    // Svisle mezery ridi segment track, ne akordeon (important prebiji emotion styl MUI).
    [theme.breakpoints.up('md')]: {
      background: 'transparent',
      border: 'none',
      margin: '0 !important',
    },
  },
  searchBarHeadText: {
    margin: 0,
    padding: 0,
  },
  searchBarHead: {
    padding: 0,
    margin: 0,
    dispay: 'flex',
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  accDetail: {
    padding: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
      padding: 0,
    },
  },
  // Panel s tlacitky Export/Novy zaznam - na mobilu viditelny, i kdyz jsou filtry sbalene.
  topActionsRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  },
  accordionWrapper: {
    flexGrow: 1,
    minWidth: 0,
  },
  topActionButton: {
    marginTop: theme.spacing(2),
  },
  icon: {
    color: newThemeObject.palette.main.sectext,
    backgroundColor: tokens.color.white,
  },
  // Cervene upozorneni nad denikem - vidi ho kazdy, kdo na denik dosahne.
  noticeBanner: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    background: tokens.color.error[100],
    border: `1px solid ${tokens.color.error[500]}`,
    borderRadius: tokens.radius.lg,
    color: tokens.color.error[700],
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(2),
    fontWeight: tokens.font.weight.semibold,
  },
  noticeIcon: {
    fontSize: '1.25rem',
    flexShrink: 0,
  },
  noticeBody: {
    flexGrow: 1,
    minWidth: 0,
  },
  noticeTitle: {
    margin: 0,
    fontWeight: tokens.font.weight.bold,
  },
  noticeText: {
    margin: 0,
    marginTop: theme.spacing(0.5),
    fontWeight: tokens.font.weight.medium,
  },
  noticeSection: {
    marginTop: theme.spacing(1.5),
  },
  noticeToggle: {
    marginTop: theme.spacing(1),
    color: 'inherit',
    font: 'inherit',
    fontWeight: tokens.font.weight.semibold,
    textDecoration: 'underline',
  },
  noticeToggleIcon: {
    fontSize: '1.25rem',
    transition: 'transform 0.2s',
  },
  noticeToggleIconOpen: {
    transform: 'rotate(180deg)',
  },
}))

const ribon = process.env.REACT_APP_ENV_RIBBON

export const TraderDiary = () => {
  const classes = useStyles()
  const { getTraderDiaries, updateOrderDiary, exportTraderDiary } = useTraderDiaryApi()
  const { t } = useTranslation()
  const isMobile = useMobileWidth()
  const [filtersExpanded, setFiltersExpanded] = useState(isMobile)
  const [noticeExpanded, setNoticeExpanded] = useState(false)
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [traderDiariesToExport, setTraderDiariesToExport] = useState<number[]>([]);
  const dispatch = useDispatch();
  const user = useSelector(state => state.auth.user)

  const [dateFrom, setDateFrom] = useState<Date>(new Date())
  const [dateTo, setDateTo] = useState<Date>(new Date())

  const showExportButton = () => {
    if (user && user.roles) {
      for (let i = 0; i < user.roles.length; i++) {
        if (user.roles[i] === 'CEO' || user.roles[i] === 'PORTF MANAGER' || user.roles[i] === 'ASSISTANT') {
          return true
        }
      }
    }
    return false
  }

  useEffect(() => {
    getTraderDiaries(dayStart(dateFrom).toISOString(), dayEnd(dateTo).toISOString());
  }, [dateFrom, dateTo])

  const traderDiaryTableRows = useTraderDiaryFilters();

  useEffect(() => {
    isMobile ? setFiltersExpanded(false) : setFiltersExpanded(true)
  }, [isMobile])

  const handleChange = () => {
    setFiltersExpanded(!filtersExpanded)
  }

  const setTraderDiaryCheck = (traderDiaryId: number, valueDiary: boolean) => {
    updateOrderDiary(traderDiaryId, !valueDiary);
  }

  const setShowConfirmationDialog = (open: boolean) => {
    let tmp = [];
    for ( let i=0; i<traderDiaryTableRows.length; i++) {
      tmp.push(traderDiaryTableRows[i].traderDiaryId);
    }
    setTraderDiariesToExport(tmp)
    setShowConfirmation(open);
  }

  const handleConfirmation = () => {
    setShowConfirmation(false);
    exportTraderDiary(traderDiariesToExport, dayStart(dateFrom).toISOString(), dayEnd(dateTo).toISOString());
    dispatch(exportTraderDiariesAction(traderDiariesToExport));
    setTraderDiariesToExport([]);
    getTraderDiaries(dayStart(dateFrom).toISOString(), dayEnd(dateTo).toISOString());
  }

  const handleCloseConfirmation = () => {
    setShowConfirmation(false);
    setTraderDiariesToExport([]);
  }

  return (
    <NewLayout pageName={t('TRADER_DIARY')}>
      <div className={classes.noticeBanner} role="alert">
        <WarningAmberRounded className={classes.noticeIcon} />
        <div className={classes.noticeBody}>
          <p className={classes.noticeTitle}>{t('DIARY_NOTICE_CHANGE_TITLE')}</p>
          <p className={classes.noticeText}>{t('DIARY_NOTICE_CHANGE_TEXT')}</p>
          <Collapse in={noticeExpanded}>
            <div className={classes.noticeSection}>
              <p className={classes.noticeTitle}>{t('DIARY_NOTICE_MARKET_TITLE')}</p>
              <p className={classes.noticeText}>{t('DIARY_NOTICE_MARKET_TEXT')}</p>
            </div>
            <div className={classes.noticeSection}>
              <p className={classes.noticeTitle}>{t('DIARY_NOTICE_CAUTION_TITLE')}</p>
              <p className={classes.noticeText}>{t('DIARY_NOTICE_CAUTION_TEXT')}</p>
              <p className={classes.noticeText}>{t('DIARY_NOTICE_CAUTION_TEXT_2')}</p>
            </div>
          </Collapse>
          <ButtonBase
            className={classes.noticeToggle}
            onClick={() => setNoticeExpanded(!noticeExpanded)}
            aria-expanded={noticeExpanded}
          >
            {t(noticeExpanded ? 'DIARY_NOTICE_SHOW_LESS' : 'DIARY_NOTICE_SHOW_MORE')}
            <ExpandMore className={classNames(classes.noticeToggleIcon, { [classes.noticeToggleIconOpen]: noticeExpanded })} />
          </ButtonBase>
        </div>
      </div>
      <TraderDiaryTiles />
      <div className={classes.topActionsRow}>
        <Accordion elevation={0} className={classNames(classes.searchBar, classes.accordionWrapper)} expanded={filtersExpanded} onChange={handleChange}>
          <AccordionSummary expandIcon={<ExpandMore />} className={classes.searchBarHead}>
            <p className={classes.searchBarHeadText}>Filtry</p>
          </AccordionSummary>
          <AccordionDetails className={classes.accDetail}>
            <TraderDiaryTableTopBar
                dateFrom={dateFrom}
                setDateFrom={setDateFrom}
                dateTo={dateTo}
                setDateTo={setDateTo}
                setShowConfirmation={setShowConfirmationDialog}
            />
          </AccordionDetails>
        </Accordion>
        {isMobile && (
          <>
            {showExportButton() && (
              <ColoredIconButton
                className={classNames(classes.icon, classes.topActionButton)}
                onClick={() => setShowConfirmationDialog(true)}
                size="small"
                icon={<DownloadIcon />}
              />
            )}
            <ColoredIconButton
              className={classes.topActionButton}
              onClick={() => dispatch(openDialog(TRADER_DIARY_INPUT_DIALOG_ID))}
              size="small"
              icon={<AddRounded />}
            />
          </>
        )}
      </div>
      <TraderDiaryDisplay
        setTraderDiaryCheck={setTraderDiaryCheck}
      />
      <TradeDiaryInputDialog />
      <FileImportDialog />
      <AlertMessageConfirmation
        alertOpen={showConfirmation}
        onOk={handleConfirmation}
        onCancle={handleCloseConfirmation}
        alertText={t('INVESTMENT_DIARY_MESSAGE')}
      />
      
    </NewLayout>
  )
}
